import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

// ✅ Formspree のフォームIDをここに設定してください
// 例: "https://formspree.io/f/xpwzabcd"
const FORMSPREE_ENDPOINT = "https://formspree.io/f/meeppnqq";

const COUNTRIES = [
  "United States", "United Kingdom", "Germany", "France", "Italy", "Spain",
  "Australia", "Canada", "China", "South Korea", "Taiwan", "Singapore",
  "Hong Kong", "Thailand", "Vietnam", "India", "Brazil", "Mexico",
  "Netherlands", "Sweden", "Switzerland", "Denmark", "Norway", "Finland",
  "New Zealand", "South Africa", "UAE", "Saudi Arabia", "Other",
];

export default function ContactSection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-8");
          }
        });
      },
      { threshold: 0.1 }
    );
    const children = sectionRef.current?.querySelectorAll(".animate-on-scroll");
    children?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    const form = e.currentTarget;
    const messageEl = form.querySelector<HTMLTextAreaElement>("textarea[name='message']");
    if (messageEl && messageEl.value.length > 500) {
      setError("メッセージは500文字以内でご入力ください。");
      return;
    }
    setSubmitting(true);

    const formData = new FormData(form);
    const payload: Record<string, string> = {};
    formData.forEach((value, key) => {
      payload[key] = value.toString();
    });

    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        const json = await res.json().catch(() => ({}));
        setError((json as { error?: string }).error ?? "送信に失敗しました。もう一度お試しください。");
      }
    } catch {
      setError("送信に失敗しました。もう一度お試しください。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-36 bg-[#fafafa]" ref={sectionRef}>
      <div className="max-w-4xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 mb-10">
          <p className="text-xs text-gray-400 tracking-[0.4em] uppercase mb-3">
            {t("contact.label")} / {t("contact.labelJa")}
          </p>
          <h2 className="text-3xl md:text-4xl font-black text-[#1a2332] leading-tight mb-2">
            {t("contact.title")}
          </h2>
          <p className="text-sm text-gray-400 tracking-widest mb-4">{t("contact.titleEn")}</p>

        </div>

        {/* Form */}
        <div className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 delay-100">
          {submitted ? (
            <div className="bg-white border border-[#2d5f4f]/20 rounded-2xl p-10 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#2d5f4f]/10 rounded-full mx-auto mb-4">
                <i className="ri-check-line text-3xl text-[#2d5f4f]"></i>
              </div>
              <h3 className="text-xl font-bold text-[#1a2332] mb-3">{t("contact.successTitle")}</h3>
              <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{t("contact.successMsg")}</p>
            </div>
          ) : (
            <form
              data-readdy-form
              id="contact-form"
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl border border-gray-100 p-8 md:p-10 space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    {t("contact.companyName")} <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="company_name"
                    required
                    className="w-full bg-[#fafafa] border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-[#1a2332] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    {t("contact.yourName")} <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full bg-[#fafafa] border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-[#1a2332] transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">
                  {t("contact.email")} <span className="text-red-400">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  className="w-full bg-[#fafafa] border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-[#1a2332] transition-colors"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-medium text-gray-600">
                    {t("contact.message")} <span className="text-red-400">*</span>
                  </label>
                  <span className={`text-xs ${charCount > 500 ? "text-red-400" : "text-gray-400"}`}>
                    {charCount} / 500
                  </span>
                </div>
                <textarea
                  name="message"
                  required
                  rows={6}
                  placeholder={t("contact.messagePlaceholder")}
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full bg-[#fafafa] border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-[#1a2332] transition-colors resize-none"
                />
                {charCount > 500 && (
                  <p className="text-xs text-red-400 mt-1">{t("contact.charLimit")}</p>
                )}
              </div>

              {error && (
                <p className="text-xs text-red-500 bg-red-50 px-4 py-3 rounded-lg">{error}</p>
              )}

              <button
                type="submit"
                disabled={submitting || charCount > 500}
                className="w-full bg-[#1a2332] text-white font-semibold text-sm py-4 rounded-full hover:bg-[#2d3f55] transition-colors duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? t("contact.submitting") : t("contact.submit")}
              </button>
            </form>
          )}
        </div>


      </div>
    </section>
  );
}
